import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PersonSkillListItem, {
  canonicalSkillId,
  personSkillDisplayName,
} from "../ListItems/PersonSkillListItem";
import PersonSkillEditModal from "../Modals/PersonSkillEditModal";
import BreadCrumb from "../Controls/BreadCrumb";
import UserData from "../Controls/UserData";
import SkillSearch from "../Controls/SkillSearch";
import Help from "../Controls/Help";
import { usePersonSkillsQuery, useRefreshPersonSkills } from "../../query/usePersonSkills";
import { openSkillForum } from "../../skillForum/openForum";

function skillNameMatches(personSkill, catalogSkill, query) {
  const needle = (query || "").trim().toLowerCase();
  if (!needle) return true;
  const names = [
    personSkill?.SkillName,
    personSkill?.skillName,
    catalogSkill?.Name,
    catalogSkill?.name,
    catalogSkill?.ShortName,
    catalogSkill?.shortName,
    catalogSkill?.ParentSkillName,
    personSkillDisplayName(catalogSkill, personSkill),
  ];
  return names.some((n) => (n || "").toLowerCase().includes(needle));
}

/** One list row per base skill; prefer the base PersonSkill when present. */
function collapseVersionChildren(personSkills, skills) {
  const byCanonical = new Map();
  for (const personSkill of personSkills) {
    const skill = skills.find((s) => s.ID === personSkill.SkillID || s.id === personSkill.SkillID);
    const key = canonicalSkillId(skill, personSkill);
    if (!(key > 0)) continue;
    const existing = byCanonical.get(key);
    if (!existing) {
      byCanonical.set(key, personSkill);
      continue;
    }
    const existingSkill = skills.find((s) => s.ID === existing.SkillID || s.id === existing.SkillID);
    const existingIsBase = !(Number(existingSkill?.ParentID || existingSkill?.parentID) > 0);
    const candidateIsBase = !(Number(skill?.ParentID || skill?.parentID) > 0);
    if (candidateIsBase && !existingIsBase) {
      byCanonical.set(key, personSkill);
      continue;
    }
    if (existingIsBase === candidateIsBase) {
      const existingExp = Number(existing.ExperienceMonths) || 0;
      const nextExp = Number(personSkill.ExperienceMonths) || 0;
      if (nextExp > existingExp) byCanonical.set(key, personSkill);
    }
  }
  return [...byCanonical.values()].sort((a, b) => {
    const an = personSkillDisplayName(
      skills.find((s) => s.ID === a.SkillID || s.id === a.SkillID),
      a
    );
    const bn = personSkillDisplayName(
      skills.find((s) => s.ID === b.SkillID || s.id === b.SkillID),
      b
    );
    return an < bn ? -1 : an > bn ? 1 : 0;
  });
}

export default function PersonSkills() {
  const navigate = useNavigate();
  const refreshSkills = useRefreshPersonSkills();
  const { data, isLoading, isError, error } = usePersonSkillsQuery();
  const [personSkillModalId, setPersonSkillModalId] = useState(null);
  const [listFilter, setListFilter] = useState("");

  const skills = data?.skills || [];
  const personSkills = useMemo(() => {
    return collapseVersionChildren(data?.personSkills || [], skills);
  }, [data?.personSkills, skills]);

  const visiblePersonSkills = useMemo(() => {
    return personSkills.filter((personSkill) =>
      skillNameMatches(
        personSkill,
        skills.find((s) => s.ID === personSkill.SkillID || s.id === personSkill.SkillID),
        listFilter
      )
    );
  }, [personSkills, skills, listFilter]);

  async function onAddSkill(skill) {
    const skillId = Number(skill?.ID || skill?.id) || 0;
    if (!(skillId > 0)) return;

    const existing = (data?.personSkills || []).find(
      (ps) => Number(ps.SkillID || ps.skillID) === skillId
    );
    if (existing) {
      setListFilter("");
      setPersonSkillModalId(existing.ID || existing.id);
      await refreshSkills();
      return;
    }

    const payload = {
      ID: 0,
      SkillID: skillId,
      SkillName: skill.Name || skill.name || "",
      InterestLevelID: 1,
      ExperienceMonths: 0,
      Employments: [],
    };
    const res = await UserData.UpdateAsync("personSkills", payload, true);
    if (!res?.ok) return;

    await refreshSkills();
    setListFilter("");
    const personSkillId =
      res.saveResp?.jsonData?.ID ||
      res.saveResp?.jsonData?.id ||
      null;
    if (personSkillId) setPersonSkillModalId(personSkillId);
  }

  async function closePersonSkillModal() {
    setPersonSkillModalId(null);
    await refreshSkills();
  }

  function openForum(personSkill) {
    const skill = skills.find((row) => row.ID === personSkill.SkillID || row.id === personSkill.SkillID);
    const skillId = canonicalSkillId(skill, personSkill);
    if (!(skillId > 0)) return;
    openSkillForum({
      skillId,
      skillName: personSkillDisplayName(skill, personSkill),
    });
    navigate(`/skillForum/${skillId}`);
  }

  const hasFilter = (listFilter || "").trim().length > 0;

  return (
    <div className="pagebody skillListPage">
      <BreadCrumb props={["Skills"]} />
      <div className="tabbody">
        <h2>Skills</h2>
        <Help id="2138" />

        <SkillSearch
          label="Skill Search"
          allowCreate
          onSelect={onAddSkill}
          onQueryChange={setListFilter}
        />
        <p>Open a skill forum to contribute questions, discuss pending submissions, or take a test.</p>

        {isLoading ? <p className="fieldHint">Loading skills…</p> : null}
        {isError ? (
          <p className="fieldHint">{error?.message || "Could not load skills."}</p>
        ) : null}

        <ul className="TopList SkillList">
          {!isLoading && !isError && visiblePersonSkills.length > 0 ? (
            visiblePersonSkills.map((personSkill) => (
              <PersonSkillListItem
                key={personSkill.ID}
                personSkill={personSkill}
                skill={skills.find((s) => s.ID === personSkill.SkillID || s.id === personSkill.SkillID)}
                onEdit={(id) => setPersonSkillModalId(id)}
                onOpenForum={openForum}
              />
            ))
          ) : !isLoading && !isError && hasFilter ? (
            <p>No skills match “{(listFilter || "").trim()}”</p>
          ) : !isLoading && !isError ? (
            <p>You have not added any skills</p>
          ) : null}
        </ul>
      </div>
      {personSkillModalId !== null ? (
        <PersonSkillEditModal
          personSkillId={personSkillModalId}
          onClose={closePersonSkillModal}
        />
      ) : null}
    </div>
  );
}
